import * as yup from 'yup';
import { ChangeEvent, useState } from 'react';
import { Lock, Search, User, UserCheck, UserPlus, Users } from 'react-feather';
import { gql } from '__generated__';
import { useTranslation } from 'react-i18next';

import {
  GetTeamsForEventQuery as GetTeamsForEvent,
  TeamInput,
} from '__generated__/graphql';

import { ErrorBag } from '../helpers';
import UI from '../UI';
import useQuery from '../../api/useQuery';
import useValidation from '../useValidation';

 type Team = GetTeamsForEvent['event']['available_teams']['data'][0];

export enum TeamMode {
  Individual = 'individual',
  Create = 'create',
  Join = 'join',
}

export const GetTeamsForEventQuery = gql(`
  query GetTeamsForEvent($eventId: ID!, $search: String) {
    event(id: $eventId) {
      id
      available_teams(per_page: 100, search: $search) {
        data {
          id
          title
        }
      }
    }
  }
`);

export const GetTeamByAccessCodeQuery = gql(`
  query GetTeamByAccessCode($eventId: ID!, $accessCode: String!) {
    event(id: $eventId) {
      id
      team_by_code(access_code: $accessCode) {
        id
        title
      }
    }
  }
`);

const teamSchema = yup.object().shape({
  title: yup.string().required(),
});

export interface TeamPickerProps {
  team?: TeamInput | null;
  originalTeam?: TeamInput | null;
  eventId: string;
  privateTeams: boolean;
  allowIndividuals: boolean;
  allowCreateTeam: boolean;
  allowJoinTeam: boolean;
  onChange: (team: TeamInput | null, mode: TeamMode) => void;
  touched?: {
    title?: boolean;
    search?: boolean;
    access_code?: boolean;
  };
  touch?: (field: string) => void;
  untouch?: (field?: string) => void;
  errors?: ErrorBag;
}

const TeamPicker = ({
  team, originalTeam, eventId, privateTeams, allowIndividuals = true, allowCreateTeam = true, allowJoinTeam = true,
  onChange, touch, untouch, touched, errors: externalErrors,
}: TeamPickerProps) => {
  const { t } = useTranslation('common');

  const [search, setSearch] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [teams, setTeams] = useState<Team[]>([]);

  const { data: getTeamsData } = useQuery(
    GetTeamsForEventQuery,
    {
      variables: {
        eventId,
        search,
      },
      skip: !search,
      onCompleted: (data) => {
        setTeams(data.event.available_teams.data);
      },
    },
  );

  const { data: getTeamData } = useQuery(
    GetTeamByAccessCodeQuery,
    {
      variables: {
        eventId,
        accessCode,
      },
      skip: !accessCode,
      onCompleted: ({ event: { team_by_code: team } }) => {
        setTeams(team ? [team] : []);

        if (team) {
          onChange({
            id: team.id,
            title: team.title,
            access_code: accessCode,
          }, TeamMode.Join);
        }
      },
    },
  );

  const getDefaultTeamMode = () => {
    if (allowIndividuals) {
      return TeamMode.Individual;
    }

    if (allowCreateTeam) {
      return TeamMode.Create;
    }

    if (allowJoinTeam) {
      return TeamMode.Join;
    }

    return null;
  };

  const [teamMode, setTeamMode] = useState(() => {
    if (team?.id) {
      return TeamMode.Join;
    }

    if (team?.title) {
      return TeamMode.Create;
    }

    return getDefaultTeamMode();
  });

  const participateIndividually = () => {
    setTeamMode(TeamMode.Individual);
    onChange(null, TeamMode.Individual);
    untouch?.();
  };

  const createTeam = () => {
    setTeamMode(TeamMode.Create);
    onChange({ title: '' }, TeamMode.Create);
    untouch?.();
  };

  const joinTeam = (id?: string | null) => {
    setTeamMode(TeamMode.Join);
    setTeams([]);
    setSearch('');
    setAccessCode('');
    onChange({ id }, TeamMode.Join);
    untouch?.();
  };

  const leaveTeam = () => {
    onChange({}, TeamMode.Join);
  };

  const handleSearch = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setSearch(value);
    onChange({}, teamMode);
  };

  const handleAccessCode = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setAccessCode(value);
    onChange({}, teamMode);
  };

  const { errors } = useValidation({
    schema: teamSchema,
    values: team,
    externalErrors,
  });

  const handleTitleChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    onChange({ title: value }, teamMode);
  };

  const showRadioButtons = (allowIndividuals ? 1 : 0) + (allowCreateTeam ? 1 : 0)
    + (allowJoinTeam ? 1 : 0) > 1 || !!originalTeam?.id;

  const teamOptions = team?.id ? (
    <UI.Checkbox
      onChange={() => leaveTeam()}
      sc={{ box: true, block: true }}
      disabled={!!accessCode}
      checked
    >
      <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.5 }}>
        <UI.Div>
          <UI.Icon>
            <Users />
          </UI.Icon>
        </UI.Div>
        <UI.Div>
          <UI.Strong>
            {team.title}
          </UI.Strong>
        </UI.Div>
      </UI.FormGrid>
    </UI.Checkbox>
  ) : (search || accessCode) && (
    <UI.FormGrid sc={{ gutter: 0.25 }}>
      {teams.map((candidate) => (
        <UI.Checkbox
          onChange={() => onChange(
            { id: candidate.id, title: candidate.title, access_code: accessCode || null },
            teamMode,
          )}
          sc={{ box: true }}
          key={candidate.id}
        >
          <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.5 }}>
            <UI.Div>
              <UI.Icon>
                <Users />
              </UI.Icon>
            </UI.Div>
            <UI.Div>
              <UI.Strong>
                {candidate.title}
              </UI.Strong>
            </UI.Div>
          </UI.FormGrid>
        </UI.Checkbox>
      ))}

      {(getTeamsData || getTeamData) && teams.length === 0 && t('team_picker.no_teams_found')}
    </UI.FormGrid>
  );

  return (
    <UI.FormGrid>
      {showRadioButtons && (
        <UI.InputGroup sc={{ mb: teamMode === TeamMode.Individual ? 0 : undefined }}>
          <UI.FormGrid sc={{ gutter: 0.25 }}>
            {originalTeam && (
              <UI.Radio
                name="team"
                onChange={() => joinTeam(originalTeam.id)}
                checked={team?.id === originalTeam.id}
                sc={{ box: true }}
              >
                <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.5 }}>
                  <UI.Div>
                    <UI.Icon>
                      <UserCheck />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {originalTeam.title}
                    </UI.Strong>
                    {t('team_picker.stay_in_current_team')}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Radio>
            )}
            {allowIndividuals && (
              <UI.Radio
                name="team"
                onChange={() => participateIndividually()}
                checked={teamMode === TeamMode.Individual}
                sc={{ box: true }}
              >
                <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.5 }}>
                  <UI.Div>
                    <UI.Icon>
                      <User />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('team_picker.participate_individually')}
                    </UI.Strong>
                    {t('team_picker.team_mode_individual_description')}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Radio>
            )}
            {allowJoinTeam && (
              <UI.Radio
                name="team"
                onChange={() => joinTeam()}
                checked={teamMode === TeamMode.Join && (!originalTeam || originalTeam.id !== team?.id)}
                sc={{ box: true }}
              >
                <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.5 }}>
                  <UI.Div>
                    <UI.Icon>
                      <UserPlus />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('team_picker.join_existing_team')}
                    </UI.Strong>
                    {t('team_picker.team_mode_join_description')}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Radio>
            )}
            {allowCreateTeam && (
              <UI.Radio
                onChange={() => createTeam()}
                name="team"
                sc={{ box: true }}
                checked={teamMode === TeamMode.Create}
              >
                <UI.FormGrid sc={{ columns: '14px 1fr', gutter: 0.5 }}>
                  <UI.Div>
                    <UI.Icon>
                      <Users />
                    </UI.Icon>
                  </UI.Div>
                  <UI.Div>
                    <UI.Strong sc={{ block: true }}>
                      {t('team_picker.create_new_team')}
                    </UI.Strong>
                    {t('team_picker.team_mode_create_description')}
                  </UI.Div>
                </UI.FormGrid>
              </UI.Radio>
            )}
          </UI.FormGrid>
        </UI.InputGroup>
      )}

      {teamMode === TeamMode.Create && (
        <UI.InputGroup sc={{ valid: !errors?.title, touched: touched?.title, mb: 0 }}>
          <UI.InputLabel htmlFor="TeamTitle">
            {t('team_picker.team_name')}
            {' '}
            <UI.RequiredMark />
          </UI.InputLabel>
          {privateTeams && allowJoinTeam && (
            <UI.InputDescription>
              {t('team_picker.team_name_private_description')}
            </UI.InputDescription>
          )}
          <UI.DebouncedInput
            type="text"
            value={team?.title || ''}
            onChange={handleTitleChange}
            name="team.title"
            onBlur={() => touch?.('title')}
            id="TeamTitle"
          />
          <UI.ErrorMessages attribute={t('team_picker.team_name')} errors={errors.title} />
        </UI.InputGroup>
      )}

      {teamMode === TeamMode.Join && (!team?.id || team.id !== originalTeam?.id) && (
        <UI.FormGrid sc={{ gutter: 0.5 }}>
          {!privateTeams && (
            <UI.InputGroup sc={{ valid: !!search, touched: touched?.search }}>
              <UI.InputLabel htmlFor="TeamSearch">
                {t('team_picker.team')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.InputWrapper sc={{ mb: 2 }}>
                <UI.Span>
                  <UI.Icon>
                    <Search />
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  value={search}
                  onChange={handleSearch}
                  name="team.search"
                  onBlur={() => touch?.('search')}
                  sc={{ pl: 5 }}
                  id="TeamSearch"
                  placeholder={t('team_picker.find_your_team')}
                />
              </UI.InputWrapper>
              {teamOptions}
            </UI.InputGroup>
          )}

          {privateTeams && (
            <UI.InputGroup sc={{ valid: !!accessCode, touched: touched?.access_code }}>
              <UI.InputLabel htmlFor="TeamAccessCode">
                {t('team_picker.access_code')}
                {' '}
                <UI.RequiredMark />
              </UI.InputLabel>
              <UI.InputDescription>
                {t('team_picker.enter_access_code_description')}
              </UI.InputDescription>
              <UI.InputWrapper sc={{ mb: 2 }}>
                <UI.Span>
                  <UI.Icon>
                    <Lock />
                  </UI.Icon>
                </UI.Span>
                <UI.DebouncedInput
                  value={accessCode}
                  onChange={handleAccessCode}
                  name="team.access_code"
                  onBlur={() => touch?.('access_code')}
                  sc={{ pl: 5 }}
                  id="TeamAccessCode"
                />
              </UI.InputWrapper>
              {teamOptions}
              <UI.ErrorMessages attribute={t('team_picker.team')} errors={errors.team} />
            </UI.InputGroup>
          )}
        </UI.FormGrid>
      )}
    </UI.FormGrid>
  );
};

export default TeamPicker;
